import { Fragment, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { RadioGroup, Transition } from "@headlessui/react";
import { api_key, authEmployeeID, sdkURL } from "../utils/constants";
import LoadingScreen from "../components/LoadingScreen";
import Failure from "../assets/images/Failure.webp";
import { ReactComponent as Loader1 } from "../assets/icons/Loader.svg";

const PulmoSurvey = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState({ id: "", questions: [] });
  const [selectedQuestion, setSelectedQuestion] = useState(1);
  const [answers, setAnswers] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await fetch(sdkURL("/survey/get-surveys"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            api_key,
            employee_id: authEmployeeID,
            scan_id: state?.scanResult?.scan_id ?? "",
            action: "after_scan",
            table_config_rows_per_page: 1,
            table_config_page_no: 1,
          }),
          signal: controller.signal,
        });
        const respJSON = await resp.json();
        if (respJSON?.statusCode?.toString().startsWith("2")) {
          const questions =
            respJSON.response?.[0]?.survey_question_details?.map?.((q) => ({
              id: q.question_id,
              question: q.question,
              options: q.answer_options,
              order: q.question_order,
            })) ?? [];
          if (questions.length < 5) throw new Error("No Questions to Answer.");
          questions.sort((a, b) => a.order - b.order);
          const ageQuestionID = questions[questions.length - 1].id;
          if (ageQuestionID.length > 0) {
            const age = Math.abs(
              new Date(
                Date.now() - new Date(state?.dob).getTime()
              ).getUTCFullYear() - 1970
            );
            const ageQuestionAns =
              age < 40
                ? "5"
                : age < 50
                ? "4"
                : age < 60
                ? "3"
                : age < 70
                ? "2"
                : "1";
            setAnswers({ [ageQuestionID]: ageQuestionAns });
            questions.pop();
          } else setAnswers({});
          setSelectedQuestion(questions[0].order ?? 1);
          setSurvey({ id: respJSON.response[0].survey_id ?? "", questions });
        } else
          throw new Error(respJSON?.message ?? "Error in Fetching Survey.");
      } catch (err) {
        console.error(err);
        if (err instanceof Error) setError(err.message);
        else if (typeof err === "string") setError(err);
        else setError("Error in Fetching Survey.");
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
  }, [state]);

  const proceed = async () => {
    setBtnLoading(true);
    setError("");
    try {
      if (selectedQuestion !== survey.questions.length)
        setSelectedQuestion((s) => s + 1);
      else {
        const resp = await fetch(sdkURL("/survey/answer-survey"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            api_key,
            employee_id: authEmployeeID,
            scan_id: state?.scanResult?.scan_id ?? "",
            survey_id: survey.id,
            action: "after_scan",
            questions_answers: answers,
          }),
        });
        const respJSON = await resp.json();
        if (respJSON?.statusCode?.toString().startsWith("2")) {
          const resp = await fetch(sdkURL("/pulmo/generate-audio-score"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              api_key,
              employee_id: authEmployeeID,
              scan_id: state?.scanResult?.scan_id ?? "",
            }),
          });
          const respJSON = await resp.json();
          if (respJSON?.statusCode?.toString().startsWith("2")) {
            const hrTimeArray = respJSON?.heartrate_time ?? [];
            let hrMin = 0;
            let hrMax = 0;
            const hrData = [];
            for (let i = 0; i < hrTimeArray.length; i++) {
              if (hrTimeArray[i] < hrTimeArray[hrMin]) hrMin = i;
              if (hrTimeArray[i] >= hrTimeArray[hrMax]) hrMax = i;
              hrData.push({ time: 5 * (i + 1), value: hrTimeArray[i] });
            }
            const rrTimeArray = respJSON?.resp_rate_time ?? [];
            let rrMin = 0;
            let rrMax = 0;
            const rrData = [];
            for (let i = 0; i < rrTimeArray.length; i++) {
              if (rrTimeArray[i] < rrTimeArray[rrMin]) rrMin = i;
              if (rrTimeArray[i] >= rrTimeArray[rrMax]) rrMax = i;
              rrData.push({ time: 5 * (i + 1), value: rrTimeArray[i] });
            }
            const pulmoResult = {
              ...(state?.pulmoResult ?? {}),
              isOADScoreGood: respJSON?.oad === false,
              fev1fvcRatio: respJSON?.["fev1/fvc"] || 0,
              prq: respJSON?.prq || 0,
              pulmoScore: respJSON?.pulmo_score ?? 0,
              hrTimeData: { data: hrData, min: hrMin, max: hrMax },
              rrTimeData: { data: rrData, min: rrMin, max: rrMax },
            };
            navigate(
              state?.selectedAssesments?.dgmer === true
                ? "/digimer-assessment"
                : "/all-combined-result",
              {
                state: { ...state, pulmoResult },
                replace: true,
              }
            );
          } else
            throw new Error(respJSON?.message ?? "Error in Fetching Result.");
        } else
          throw new Error(
            respJSON?.message ?? "Error in Saving Survey answers."
          );
      }
    } catch (err) {
      console.error(err);
      if (err instanceof Error) setError(err.message);
      else if (typeof err === "string") setError(err);
      else setError("Error in Saving Survey answers.");
    } finally {
      setBtnLoading(false);
    }
  };

  return loading ? (
    <LoadingScreen style={{ minHeight: "60vh" }} />
  ) : error.length > 0 ? (
    <section className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-black text-center">
      <img className="shrink-0 h-48" src={Failure} alt="Failure..." />
      <p className="mt-8 text-secondary text-xl font-bold">
        Sorry, we failed to get Results
      </p>
      <p className="mt-4 max-h-40 overflow-auto whitespace-pre-line text-white font-light">
        {error}
      </p>
      <Link
        //@ts-ignore
        to={-2}
        className="mt-8 w-60 px-5 py-2.5 rounded-full bg-white text-primary font-medium"
      >
        Retry
      </Link>
    </section>
  ) : (
    <section className="grow flex flex-col justify-between tab-screen">
      <div className="p-8 pt-0">
        <h3 className="text-white text-left text-lg font-semibold">
          Quick Questionnaire
        </h3>
        <div className="mt-6 flex">
          {survey.questions.map((q) => (
            <Transition
              as={Fragment}
              key={q.id}
              show={q.order === selectedQuestion}
              enter="transition transform ease-linear duration-300"
              enterFrom="opacity-0 translate-x-full"
              enterTo="opacity-50 translate-x-0"
              leave="transition transform ease-linear duration-300"
              leaveFrom="opacity-50 translate-x-0"
              leaveTo="opacity-0 -translate-x-full"
            >
              <RadioGroup
                className="shrink-0 w-full"
                value={answers[q.id] ?? ""}
                onChange={(ans) => setAnswers((a) => ({ ...a, [q.id]: ans }))}
              >
                <RadioGroup.Label
                  as="h3"
                  className="mb-8 text-secondary text-sm font-semibold"
                >
                  {q.question}
                </RadioGroup.Label>
                {Object.entries(q.options).map(([point, opt], index) => (
                  <RadioGroup.Option
                    className="mt-6 flex items-center gap-x-2"
                    key={`${q.id}-option-${index}`}
                    value={point}
                  >
                    {({ checked }) => (
                      <>
                        <div
                          className={`shrink-0 h-5 w-5 rounded-md ${
                            checked
                              ? "bg-secondary border-2"
                              : "bg-white border"
                          } border-secondary`}
                        />
                        <span className="text-white text-xs">{opt}</span>
                      </>
                    )}
                  </RadioGroup.Option>
                ))}
              </RadioGroup>
            </Transition>
          ))}
        </div>
      </div>

      <div className="p-8 flex flex-col gap-y-6 text-center">
        <button
          className="lg:w-[210px] md:w-[210px] w-full mx-auto rounded-full outline-none px-5 h-9 mt-3 bg-white border border-black text-black flex text-sm font-medium justify-center items-center"
          type="button"
          onClick={proceed}
          disabled={
            btnLoading ||
            !(answers[survey.questions[selectedQuestion - 1]?.id]?.length > 0)
          }
        >
          {selectedQuestion !== survey.questions.length ? "Next" : "Save"}
          {btnLoading && <Loader1 className="shrink-0 h-3 w-3 ml-2" />}
        </button>
        <button
          className="w-[185px] mx-auto rounded-full outline-none px-5 h-9 mt-2 bg-black text-white tetx-sm font-medium flex justify-center items-center"
          type="button"
          onClick={() => {
            if (selectedQuestion === 1) navigate(-1);
            else setSelectedQuestion((s) => (s === 1 ? 1 : s - 1));
          }}
          disabled={btnLoading}
        >
          {selectedQuestion === 1 ? "Cancel" : "Back"}
        </button>
      </div>
    </section>
  );
};

export default PulmoSurvey;
